import React from 'react';

const Badge = ({ achievement, size = 'medium', showProgress = false, unlocked = false }) => {
  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return '#95a5a6'; // Gray
      case 'rare':
        return '#3498db'; // Blue
      case 'epic':
        return '#9b59b6'; // Purple
      case 'legendary':
        return '#f39c12'; // Gold
      default:
        return '#95a5a6';
    }
  };

  const getRarityGradient = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
      case 'rare':
        return 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)';
      case 'epic':
        return 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)';
      case 'legendary':
        return 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)';
      default:
        return 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
    }
  };

  const sizeMap = {
    small: { container: 80, icon: 28, fontSize: 10 },
    medium: { container: 100, icon: 36, fontSize: 11 },
    large: { container: 140, icon: 50, fontSize: 12 }
  };

  const dimensions = sizeMap[size] || sizeMap.medium;

  const containerStyle = {
    position: 'relative',
    width: dimensions.container,
    height: dimensions.container,
    display: 'inline-block',
    margin: '8px'
  };

  const badgeStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '12px',
    background: unlocked ? getRarityGradient(achievement.rarity) : '#2c3e50',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    boxShadow: unlocked
      ? `0 4px 15px ${getRarityColor(achievement.rarity)}40`
      : '0 2px 8px rgba(0,0,0,0.3)',
    border: unlocked ? `2px solid ${getRarityColor(achievement.rarity)}` : '2px solid #34495e',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    opacity: unlocked ? 1 : 0.5,
    filter: unlocked ? 'none' : 'grayscale(100%)',
    position: 'relative',
    overflow: 'hidden'
  };

  const iconStyle = {
    fontSize: dimensions.icon,
    marginBottom: '4px',
    filter: unlocked ? 'none' : 'brightness(0.5)'
  };

  const nameStyle = {
    fontSize: dimensions.fontSize,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
    lineHeight: 1.2,
    marginTop: '4px'
  };

  const rarityBadgeStyle = {
    position: 'absolute',
    top: 4,
    right: 4,
    fontSize: 8,
    padding: '2px 6px',
    borderRadius: '4px',
    background: 'rgba(0,0,0,0.6)',
    color: getRarityColor(achievement.rarity),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const progressBarStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: 'rgba(0,0,0,0.3)',
    overflow: 'hidden'
  };

  const progressFillStyle = {
    height: '100%',
    width: `${achievement.completionPercentage || 0}%`,
    background: getRarityColor(achievement.rarity),
    transition: 'width 0.5s ease'
  };

  const lockedOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: dimensions.icon,
    pointerEvents: 'none'
  };

  return (
    <div style={containerStyle}>
      <div
        style={badgeStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = unlocked
            ? `0 8px 25px ${getRarityColor(achievement.rarity)}60`
            : '0 4px 12px rgba(0,0,0,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = unlocked
            ? `0 4px 15px ${getRarityColor(achievement.rarity)}40`
            : '0 2px 8px rgba(0,0,0,0.3)';
        }}
        title={`${achievement.name}\n${achievement.description}${
          achievement.points ? `\n+${achievement.points} points` : ''
        }`}
      >
        <div style={rarityBadgeStyle}>{achievement.rarity}</div>
        <div style={iconStyle}>{achievement.icon}</div>
        <div style={nameStyle}>{achievement.name}</div>
        
        {!unlocked && (
          <div style={lockedOverlayStyle}>🔒</div>
        )}
        
        {showProgress && !unlocked && achievement.progress && (
          <div style={progressBarStyle}>
            <div style={progressFillStyle} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Badge;
